import { createRootRoute, Outlet, useNavigate, useLocation } from '@tanstack/react-router';
import { useEffect, useState, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Header } from '@/components/layout/Header';
import { Sidebar } from '@/components/layout/Sidebar';
import { LoadingScreen } from '@/components/feedback/LoadingScreen';

/**
 * @graph-node {Page}
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business アプリのルートレイアウト。認証ガード・サイドバー・ヘッダーを含むシェルレイアウト
 * @graph-connects useAuth [calls] 認証状態管理フック
 * @graph-connects Header [calls] ヘッダー描画
 * @graph-connects Sidebar [calls] サイドバー描画
 * @graph-connects LoadingScreen [calls] ローディング画面描画
 */
export const Route = createRootRoute({
  component: RootLayout,
});

/** @graph-connects none */
function RootLayout() {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const isLoginPage = location.pathname === '/login';

  // location.pathname / navigatedRef ガード: 認証 redirect useEffect 内で navigate を
  // 連続発火させない。location.pathname を deps に含めると route transition 中に
  // 多重 render で effect が再発火し、auth re-fetch 等で isLoading / isAuthenticated
  // が変動するシナリオも合わさって `history.notify` → `router.load` →
  // `setIsTransitioning` の同期連発で Minified React error #185 を起こす
  // （docs/web/product-innovation-lab.md「1 回ガード ref」規則）。
  // __root は SPA 起動中ずっと mount されるため、auth 回復時に navigatedRef を
  // リセットして次回 auth 失敗時にも redirect が走るようにする。
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;
  const navigatedRef = useRef(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigatedRef.current = false;
      return;
    }
    if (navigatedRef.current) return;
    if (isLoginPage || isLoading) return;
    navigatedRef.current = true;
    void navigate({ to: '/login', search: { redirect: pathnameRef.current } });
  }, [isAuthenticated, isLoading, isLoginPage, navigate]);

  // ローディング中
  if (isLoading && !isLoginPage) {
    return <LoadingScreen />;
  }

  // ログインページはレイアウトなし
  if (isLoginPage) {
    return <Outlet />;
  }

  // 未認証はログインページへリダイレクト（上のuseEffectで処理）
  if (!isAuthenticated) {
    return <LoadingScreen />;
  }

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: 'var(--color-bg-base)' }}>
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={() => {
          setIsSidebarOpen(false);
        }}
      />
      <div className="flex flex-1 flex-col overflow-hidden">
        <Header
          onMenuToggle={() => {
            setIsSidebarOpen(!isSidebarOpen);
          }}
        />
        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
